import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, Lock, CheckCircle, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function ProGateModal({ 
  isOpen, 
  onClose, 
  featureName = "this feature",
  onUnlock 
}) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [showDemoInput, setShowDemoInput] = useState(false);
  const [unlockSuccess, setUnlockSuccess] = useState(false);

  const handleUnlock = async () => {
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setIsUnlocking(true);
    try {
      const result = await onUnlock(email.trim(), demoCode.trim() || null);
      
      if (result.success) {
        setUnlockSuccess(true);
        toast.success("Pro unlocked successfully!");
        setTimeout(() => {
          onClose();
          setUnlockSuccess(false);
        }, 1500);
      } else {
        toast.error(result.message || "Could not verify subscription");
      }
    } catch (error) {
      toast.error("Failed to unlock Pro");
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleUpgrade = () => {
    onClose();
    navigate("/pricing");
  };

  if (unlockSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="font-heading text-xl text-sage-900 mb-2">Pro Unlocked!</h3>
            <p className="font-body text-muted-foreground">You now have access to all Pro features.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Lock className="w-5 h-5 text-amber-600" />
            </div>
            <DialogTitle className="font-heading text-xl">Upgrade to Pro to create unlimited challenges</DialogTitle>
          </div>
          <DialogDescription className="font-body text-sage-700">
            Shared challenge links are always free to play.
          </DialogDescription>
        </DialogHeader>

        {/* Free sharing reminder */}
        <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <Share2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
          <p className="font-body text-sm text-green-700">
            You can still share free practice links with students and family.
          </p>
        </div>

        <div className="space-y-4 py-2">
          {/* Upgrade button - Primary action */}
          <Button
            onClick={handleUpgrade}
            className="w-full rounded-full py-6 bg-ember hover:bg-ember/90"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade to Pro
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">or unlock with email</span>
            </div>
          </div>

          {/* Unlock with email - Secondary */}
          <div className="space-y-3">
            <Label htmlFor="email" className="font-body text-sm">Already subscribed?</Label>
            <div className="flex gap-2">
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-lg flex-1"
              />
              <Button
                onClick={handleUnlock}
                disabled={isUnlocking || !email.trim()}
                variant="outline"
                className="rounded-lg"
              >
                {isUnlocking ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Unlock"
                )}
              </Button>
            </div>
            
            {showDemoInput && (
              <Input
                type="text"
                placeholder="Demo code (optional)"
                value={demoCode}
                onChange={(e) => setDemoCode(e.target.value)}
                className="rounded-lg"
              />
            )}
            
            <button
              onClick={() => setShowDemoInput(!showDemoInput)}
              className="text-xs text-muted-foreground hover:text-sage-900 w-full text-center"
            >
              {showDemoInput ? "Hide demo code input" : "Have a demo code?"}
            </button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            No login required.
          </p>
        </div>

        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose} className="rounded-full">
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
